// Brass Eye: this was two inline IIFEs, so the tabs went dead after every variant
// change. As a custom element, connectedCallback re-runs when the block is replaced.
// When it's morphed in place instead, the MutationObserver below catches it before
// the browser paints, so the collapsed frame is never drawn.
class HoverTabs extends HTMLElement {
  static RENDERS_TAB = '3';

  // Remembers which tab was open so a variant change doesn't snap back to tab 1.
  // Lives on the class, not the element, because the element gets thrown away and
  // rebuilt whenever the section re-renders.
  static openTab = new Map();

  // Backstop only. The observer does the real work; these cover anything it misses,
  // like an image that finishes loading late.
  static SETTLE_DELAYS = [250, 800];

  connectedCallback() {
    this.key = this.getAttribute('id') || 'hover-tabs';

    if (!this.bound) {
      this.onClick = this.onClick.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.bound = true;
    }

    window.addEventListener('resize', this.onResize);
    document.addEventListener('change', this.onVariantChange);

    this.init();
    this.watch();

    // Re-measure once webfonts land, otherwise the bar is sized against the fallback face
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        const current = this.querySelector('.tab-header.active');
        if (current && this.isConnected) this.moveUnderline(current);
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('change', this.onVariantChange);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  watch() {
    if (this.observer) return;
    this.observer = new MutationObserver(() => {
      if (!this.writing) this.reconcile();
    });
    this.observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'src'],
    });
  }

  // Runs before paint, so a half-morphed state never reaches the screen
  reconcile() {
    const remembered = HoverTabs.openTab.get(this.key);
    const activeHeader = this.querySelector('.tab-header.active');
    const tabOff = remembered && (!activeHeader || activeHeader.dataset.tab !== remembered);
    const barGone = !this.underline || !this.underline.isConnected;
    const headersStale =
      !this.tabs || !this.tabs.length || this.tabs[0] !== this.querySelector('.tab-header');

    this.writing = true;
    try {
      if (tabOff || barGone || headersStale) this.init();
      else this.applyRenders();
    } finally {
      this.writing = false;
    }
  }

  init() {
    this.tabs = Array.from(this.querySelectorAll('.tab-header'));
    this.tabHeaders = this.querySelector('.tab-headers');
    this.wrapper = this.querySelector('.tab-contents-wrapper');
    if (!this.tabs.length || !this.tabHeaders) return;

    // A morph strips this span, because it only ever exists in the DOM and never
    // in the server HTML, so rebuild it whenever it's gone missing.
    if (!this.underline || !this.underline.isConnected) {
      this.underline = document.createElement('span');
      this.underline.style.position = 'absolute';
      this.underline.style.bottom = '0';
      this.underline.style.height = '1px';
      this.underline.style.backgroundColor = '#C01F25';
      this.tabHeaders.appendChild(this.underline);
    }

    // Re-adding an identical listener reference is a no-op, so this is safe to repeat
    this.tabs.forEach((tab) => tab.addEventListener('click', this.onClick));

    const remembered = HoverTabs.openTab.get(this.key);
    const target = remembered ? this.tabs.find((t) => t.dataset.tab === remembered) : null;

    if (target && !target.classList.contains('active')) {
      this.selectTab(target, false);
    } else {
      const active = this.querySelector('.tab-header.active');
      if (active) this.moveUnderline(active);
    }

    this.applyRenders();
  }

  moveUnderline(tab) {
    const text = tab.querySelector('span');
    if (!text || !this.underline) return;
    this.underline.style.left = tab.offsetLeft + text.offsetLeft + 'px';
    this.underline.style.width = text.offsetWidth + 'px';
  }

  onResize() {
    const active = this.querySelector('.tab-header.active');
    if (active) this.moveUnderline(active);
  }

  onClick(event) {
    this.selectTab(event.currentTarget, true);
  }

  onVariantChange(event) {
    const el = event.target;
    if (!el || !el.matches || !el.matches('variant-picker input[type="radio"]')) return;
    HoverTabs.SETTLE_DELAYS.forEach((ms) => {
      setTimeout(() => {
        if (this.isConnected) this.reconcile();
      }, ms);
    });
  }

  selectTab(tab, animate) {
    const target = tab.dataset.tab;

    this.tabs.forEach((t) => t.classList.remove('active'));
    tab.classList.add('active');
    HoverTabs.openTab.set(this.key, target);

    this.underline.style.transition = animate ? 'left 0.4s ease, width 0.4s ease' : 'none';
    this.moveUnderline(tab);

    const activeContent = this.querySelector('.tab-content.active');
    const newContent = this.querySelector('.tab-content[data-tab-content="' + target + '"]');
    const wrapper = this.wrapper;
    if (!activeContent || !newContent || activeContent === newContent) return;

    // Instant path, used when restoring after a re-render
    if (!animate || !wrapper) {
      activeContent.classList.remove('active');
      activeContent.style.display = 'none';
      newContent.classList.add('active');
      newContent.style.display = 'block';
      return;
    }

    wrapper.style.height = activeContent.offsetHeight + 'px';

    newContent.style.display = 'block';
    newContent.style.opacity = 0;
    newContent.style.position = 'absolute';
    newContent.style.top = '0';

    wrapper.style.height = newContent.scrollHeight + 'px';

    setTimeout(() => {
      activeContent.style.opacity = 0;
      newContent.style.opacity = 1;
    }, 20);

    setTimeout(() => {
      activeContent.classList.remove('active');
      activeContent.style.display = 'none';
      activeContent.style.opacity = '';
      activeContent.style.position = '';
      newContent.classList.add('active');
      newContent.style.position = '';
      newContent.style.opacity = '';
      wrapper.style.height = '';
      this.underline.style.transition = 'none';
    }, 400);
  }

  applyRenders() {
    const mapEl = this.querySelector('[data-renders-map]');
    if (!mapEl) return;

    let map;
    try {
      map = JSON.parse(mapEl.textContent);
    } catch (e) {
      return;
    }

    const checked = Array.from(
      document.querySelectorAll('variant-picker input[type="radio"]:checked')
    ).map((input) => input.value);

    const entry = map.find((item) => checked.includes(item.size));
    if (!entry) return;

    // Only write when it actually differs, otherwise the observer sees our own
    // change and we churn in a loop
    this.querySelectorAll('.tab-content[data-tab-content="' + HoverTabs.RENDERS_TAB + '"] img')
      .forEach((img, index) => {
        const next = entry.images[index];
        if (next && img.getAttribute('src') !== next) img.src = next;
      });
  }
}

if (!customElements.get('hover-tabs')) customElements.define('hover-tabs', HoverTabs);